import { useState, useEffect } from "react";
import {
  Users, Building2, LayoutDashboard, Key,
  ScrollText, ToggleLeft, ToggleRight,
  RefreshCw, AlertTriangle, X, Plus, Pencil, Eye, EyeOff
} from "lucide-react";
import DashboardLayout from "../../../components/DashboardLayout";
import { supabase } from "../../../lib/supabaseClient";
import { supabaseAdmin } from "../../../lib/supabaseAdmin";

const NAV = [
  { label: "Dashboard",     icon: LayoutDashboard, path: "/dashboards/superadmin" },
  { label: "Properties",    icon: Building2, path: "/dashboards/superadmin/properties" },
  { label: "Users",         icon: Users, path: "/dashboards/superadmin/users", active: true },
  { label: "Valet Codes",   icon: Key, path: "/dashboards/superadmin/valet-codes" },
  { label: "Activity Logs", icon: ScrollText, path: "/dashboards/superadmin/activity-logs" }
];

// ─── Role Config ──────────────────────────────────────────────────────────────
const ROLE_CONFIG = {
  super_admin: { label: "Super Admin", color: "#6a1b9a", bg: "#f3e5f5", border: "#ce93d8" },
  hoa_admin:   { label: "HOA Admin",   color: "#1565c0", bg: "#e3f2fd", border: "#90caf9" },
  resident:    { label: "Resident",    color: "var(--g600)", bg: "var(--g50)", border: "var(--g200)" },
  valet:       { label: "Valet",       color: "#e65100", bg: "#fff3e0", border: "#ffb74d" },
};

function RoleBadge({ role }) {
  const c = ROLE_CONFIG[role] || ROLE_CONFIG.resident;
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, letterSpacing: ".5px", textTransform: "uppercase",
      padding: "3px 10px", borderRadius: 20,
      background: c.bg, color: c.color, border: `1px solid ${c.border}`,
    }}>{c.label}</span>
  );
}

function StatusBadge({ active }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, letterSpacing: ".5px", textTransform: "uppercase",
      padding: "3px 10px", borderRadius: 20,
      background: active ? "var(--g50)" : "#fef2f2",
      color: active ? "var(--g600)" : "#dc2626",
      border: `1px solid ${active ? "var(--g200)" : "#fecaca"}`,
    }}>
      {active ? "Active" : "Inactive"}
    </span>
  );
}

// ─── Confirm Dialog ───────────────────────────────────────────────────────────
function ConfirmDialog({ title, message, confirmLabel, confirmColor = "#dc2626", onConfirm, onCancel }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100, padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 14, padding: 24, width: "100%", maxWidth: 360, boxShadow: "0 8px 40px rgba(0,0,0,.16)", animation: "fadeUp .18s ease" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <AlertTriangle size={18} color="#dc2626" />
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--ink)" }}>{title}</div>
        </div>
        <div style={{ fontSize: 13, color: "var(--mut)", lineHeight: 1.6, marginBottom: 20 }}>{message}</div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onCancel} style={{ flex: 1, padding: "10px", borderRadius: 9, border: "1.5px solid var(--ln)", background: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "var(--mut)" }}>Cancel</button>
          <button onClick={onConfirm} style={{ flex: 1, padding: "10px", borderRadius: 9, border: "none", background: confirmColor, cursor: "pointer", fontSize: 13, fontWeight: 700, color: "#fff" }}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}


function UserModal({ mode, user, properties, onClose, onSaved }) {
  const isEdit = mode === "edit";

  const [f, setF] = useState({
    full_name:   user?.full_name   || "",
    email:       user?.email       || "",
    password:    "",
    role:        user?.role        || "resident",
    property_id: user?.property_id || "",
    unit_number: user?.unit_number || "",
    is_active:   user?.is_active   ?? true,
  });

  const [errors,setErrors] = useState({});
  const [loading,setLoading] = useState(false);
  const [apiErr,setApiErr] = useState("");
  const [showPw,setShowPw] = useState(false);

  const upd = (k,v)=>{
    setF(p=>({...p,[k]:v}));
    setErrors(p=>({...p,[k]:""}));
    setApiErr("");
  };

  const validate = () => {
    const e = {};
    if(!f.full_name.trim()) e.full_name="Full name required";
    if(!isEdit && !f.email.trim()) e.email="Email required";
    if(!isEdit && f.password.length<6) e.password="Min 6 characters";
    if(!f.role) e.role="Role required";
    if(f.role!=="super_admin" && !f.property_id)
      e.property_id="Property required";
    if(f.role==="resident"){
      if(!f.unit_number) e.unit_number="Unit number required";
      else if(Number.isNaN(Number(f.unit_number)))
        e.unit_number="Unit must be a number";
    }
    return e;
  };

  const validateUnitRules = async ()=>{
    if(f.role!=="resident") return;
    const unit = Number.parseInt(f.unit_number,10);
    if(unit<1){
      throw new Error("Unit must be greater than 0");
    }
    const {data:prop,error:propErr}=await supabase
      .from("properties")
      .select("total_units")
      .eq("id",f.property_id)
      .maybeSingle();
    if(propErr||!prop){
      throw new Error("Property data not found.");
    }
    if(unit>prop.total_units){
      throw new Error(`Unit must be between 1 and ${prop.total_units}.`);
    }
    const {data:existing}=await supabase
      .from("profiles")
      .select("id")
      .eq("property_id",f.property_id)
      .eq("unit_number",unit)
      .maybeSingle();
    if(existing && existing.id!==user?.id){
      throw new Error(`Unit ${unit} already has a resident.`);
    }
  };

  const submit = async()=>{
    const e = validate();
    if(Object.keys(e).length){
      setErrors(e);
      return;
    }
    setLoading(true);
    setApiErr("");
    try{
      await validateUnitRules();
      const unitValue =
        f.role==="resident"
        ? Number.parseInt(f.unit_number,10)
        : null;
      if(isEdit){
        const {error}=await supabase
          .from("profiles")
          .update({
            full_name:f.full_name.trim(),
            role:f.role,
            property_id:f.role==="super_admin"?null:f.property_id,
            unit_number:unitValue,
            is_active:f.is_active,
            updated_at:new Date().toISOString()
          })
          .eq("id",user.id);
        if(error) throw error;
      }else{
        const {data:authData,error:authErr}=await supabaseAdmin.auth.signUp({
          email:f.email.trim().toLowerCase(),
          password:f.password
        });
        if(authErr) throw authErr;
        if(!authData?.user?.id)
          throw new Error("User creation failed.");
        await new Promise(r=>setTimeout(r,800));
        const {error:profErr}=await supabase
          .from("profiles")
          .upsert({
            id:authData.user.id,
            email:f.email.trim().toLowerCase(),
            full_name:f.full_name.trim(),
            role:f.role,
            property_id:f.role==="super_admin"?null:f.property_id,
            unit_number:unitValue,
            is_active:true,
            updated_at:new Date().toISOString()
          },{onConflict:"id"});
        if(profErr) throw profErr;
      }
      onSaved();
      onClose();
    }catch(err){
      setApiErr(err.message||"Something went wrong.");
    }
    setLoading(false);
  };

  const needsProperty = f.role!=="super_admin";
  const needsUnit = f.role==="resident";

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.35)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 16, padding: 28, width: "100%", maxWidth: 460, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 8px 40px rgba(0,0,0,.14)", animation: "fadeUp .2s ease" }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 22 }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase", color: "#6a1b9a", marginBottom: 4 }}>Super Admin</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: "var(--ink)" }}>{isEdit ? "Edit User" : "Create User"}</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--mut)", padding: 4 }}>
            <X size={20} />
          </button>
        </div>

        {/* API Error Banner */}
        {apiErr && (
          <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#dc2626", marginBottom: 16 }}>
            {apiErr}
          </div>
        )}

        {/* Full Name */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: "var(--ink)", display: "block", marginBottom: 5 }}>Full Name *</label>
          <input
            type="text"
            value={f.full_name}
            onChange={e => upd("full_name", e.target.value)}
            placeholder="e.g. John Smith"
            style={{ width: "100%", padding: "10px 12px", borderRadius: 8, fontSize: 13, border: `1.5px solid ${errors.full_name ? "#fca5a5" : "var(--ln)"}`, outline: "none", background: "#fafafa", color: "var(--ink)", boxSizing: "border-box" }}
          />
          {errors.full_name && <div style={{ fontSize: 11, color: "#dc2626", marginTop: 4 }}>{errors.full_name}</div>}
        </div>

        {/* Email — create only */}
        {!isEdit && (
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: "var(--ink)", display: "block", marginBottom: 5 }}>Email *</label>
            <input
              type="email"
              value={f.email}
              onChange={e => upd("email", e.target.value)}
              placeholder="user@example.com"
              style={{ width: "100%", padding: "10px 12px", borderRadius: 8, fontSize: 13, border: `1.5px solid ${errors.email ? "#fca5a5" : "var(--ln)"}`, outline: "none", background: "#fafafa", color: "var(--ink)", boxSizing: "border-box" }}
            />
            {errors.email && <div style={{ fontSize: 11, color: "#dc2626", marginTop: 4 }}>{errors.email}</div>}
          </div>
        )}

        {/* Password — create only */}
        {!isEdit && (
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: "var(--ink)", display: "block", marginBottom: 5 }}>Password *</label>
            <div style={{ position: "relative" }}>
              <input
                type={showPw ? "text" : "password"}
                value={f.password}
                onChange={e => upd("password", e.target.value)}
                placeholder="Min 6 characters"
                style={{ width: "100%", padding: "10px 40px 10px 12px", borderRadius: 8, fontSize: 13, border: `1.5px solid ${errors.password ? "#fca5a5" : "var(--ln)"}`, outline: "none", background: "#fafafa", color: "var(--ink)", boxSizing: "border-box" }}
              />
              <button
                type="button"
                onClick={() => setShowPw(p => !p)}
                style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--mut)" }}
              >
                {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
            {errors.password && <div style={{ fontSize: 11, color: "#dc2626", marginTop: 4 }}>{errors.password}</div>}
          </div>
        )}

        {/* Role */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: "var(--ink)", display: "block", marginBottom: 5 }}>Role *</label>
          <select
            value={f.role}
            onChange={e => upd("role", e.target.value)}
            style={{ width: "100%", padding: "10px 12px", borderRadius: 8, fontSize: 13, border: `1.5px solid ${errors.role ? "#fca5a5" : "var(--ln)"}`, outline: "none", background: "#fafafa", color: "var(--ink)", boxSizing: "border-box" }}
          >
            <option value="super_admin">Super Admin</option>
            <option value="hoa_admin">HOA Admin</option>
            <option value="resident">Resident</option>
          </select>
          {errors.role && <div style={{ fontSize: 11, color: "#dc2626", marginTop: 4 }}>{errors.role}</div>}
        </div>

        {/* Property */}
        {needsProperty && (
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: "var(--ink)", display: "block", marginBottom: 5 }}>Property *</label>
            <select
              value={f.property_id}
              onChange={e => {
                upd("property_id", e.target.value);
                upd("unit_number", "");
              }}
              style={{ width: "100%", padding: "10px 12px", borderRadius: 8, fontSize: 13, border: `1.5px solid ${errors.property_id ? "#fca5a5" : "var(--ln)"}`, outline: "none", background: "#fafafa", color: "var(--ink)", boxSizing: "border-box" }}
            >
              <option value="">— Select property —</option>
              {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            {errors.property_id && <div style={{ fontSize: 11, color: "#dc2626", marginTop: 4 }}>{errors.property_id}</div>}
          </div>
        )}

        {/* Unit Number */}
        {needsUnit && (
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: "var(--ink)", display: "block", marginBottom: 5 }}>Unit Number *</label>
            <input
              type="text"
              value={f.unit_number}
              onChange={e => upd("unit_number", e.target.value.replace(/\D/g, ""))}
              placeholder="e.g. 5"
              style={{ width: "100%", padding: "10px 12px", borderRadius: 8, fontSize: 13, border: `1.5px solid ${errors.unit_number ? "#fca5a5" : "var(--ln)"}`, outline: "none", background: "#fafafa", color: "var(--ink)", boxSizing: "border-box" }}
            />
            {errors.unit_number && <div style={{ fontSize: 11, color: "#dc2626", marginTop: 4 }}>{errors.unit_number}</div>}
          </div>
        )}

        {/* Status toggle — edit only */}
        {isEdit && (
          <div style={{ marginBottom: 14, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", borderRadius: 9, background: "#fafafa", border: "1px solid var(--ln)" }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>Account Status</div>
              <div style={{ fontSize: 12, color: "var(--mut)" }}>{f.is_active ? "Active — user can log in" : "Inactive — access denied"}</div>
            </div>
            <button
              onClick={() => upd("is_active", !f.is_active)}
              style={{ background: "none", border: "none", cursor: "pointer", color: f.is_active ? "var(--g600)" : "#dc2626" }}
            >
              {f.is_active ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
            </button>
          </div>
        )}

        {/* Email display — edit only */}
        {isEdit && user?.email && (
          <div style={{ marginBottom: 14, padding: "10px 14px", borderRadius: 9, background: "#f5f5f5", border: "1px solid var(--ln)" }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--mut)", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 3 }}>Email (cannot change)</div>
            <div style={{ fontSize: 13, color: "var(--ink)" }}>{user.email}</div>
          </div>
        )}

        {/* Buttons */}
        <div style={{ display: "flex", gap: 10, marginTop: 22 }}>
          <button
            onClick={onClose}
            style={{ flex: 1, padding: "11px", borderRadius: 10, border: "1.5px solid var(--ln)", background: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "var(--mut)" }}
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={loading}
            style={{ flex: 2, padding: "11px", borderRadius: 10, border: "none", background: loading ? "var(--g300)" : "var(--g600)", cursor: loading ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700, color: "#fff" }}
          >
            {loading ? (isEdit ? "Saving…" : "Creating…") : (isEdit ? "Save Changes" : "Create User")}
          </button>
        </div>

      </div>
    </div>
  );
}


// ─── User Row ─────────────────────────────────────────────────────────────────
function UserRow({ user, propMap, onToggle, onEdit, isValet }) {
  const [toggling,      setToggling]      = useState(false);
  const [confirmToggle, setConfirmToggle] = useState(false);

  const handleConfirmed = async () => {
    setConfirmToggle(false); setToggling(true);
    await onToggle(user.id, !user.is_active);
    setToggling(false);
  };

  const role        = isValet ? "valet" : user.role;
  const propName    = user.property_id ? (propMap[user.property_id] || "—") : "—";
  const displayName = user.full_name || user.email || user.label || user.code || "—";
  const displaySub  = isValet
    ? `Code: ${user.code}${user.property_id ? " · " + propName : ""}`
    : [user.email, user.full_name ? null : null, user.property_id ? propName : null, user.unit_number ? `Unit #${user.unit_number}` : null].filter(Boolean).join(" · ");

  return (
    <>
      <div style={{
        background: "#fff", borderRadius: 12,
        border: `1.5px solid ${user.is_active ? "var(--ln)" : "#fecaca"}`,
        marginBottom: 8, padding: "13px 16px",
        display: "flex", alignItems: "center", gap: 12,
        boxShadow: "0 1px 4px rgba(0,0,0,.04)",
        opacity: user.is_active ? 1 : 0.75,
      }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10, flexShrink: 0,
          background: ROLE_CONFIG[role]?.bg || "#f5f5f5",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 14, fontWeight: 700, color: ROLE_CONFIG[role]?.color || "var(--mut)",
        }}>
          {displayName[0].toUpperCase()}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: "var(--ink)", marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {displayName}
          </div>
          <div style={{ fontSize: 11.5, color: "var(--mut)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {displaySub}
          </div>
        </div>

        <RoleBadge role={role} />
        <StatusBadge active={user.is_active} />

        {!isValet && (
          <button onClick={() => onEdit(user)} style={{ background: "none", border: "1.5px solid var(--ln)", borderRadius: 8, cursor: "pointer", color: "var(--mut)", padding: "5px 8px", display: "flex", alignItems: "center" }}>
            <Pencil size={13} />
          </button>
        )}

        <button onClick={() => setConfirmToggle(true)} disabled={toggling}
          style={{ background: "none", border: "none", cursor: toggling ? "not-allowed" : "pointer", color: user.is_active ? "var(--g600)" : "#dc2626", padding: "2px 4px", flexShrink: 0 }}>
          {toggling
            ? <RefreshCw size={17} style={{ animation: "spin 1s linear infinite" }} />
            : user.is_active ? <ToggleRight size={22} /> : <ToggleLeft size={22} />
          }
        </button>
      </div>

      {confirmToggle && (
        <ConfirmDialog
          title={user.is_active ? "Deactivate User?" : "Activate User?"}
          message={user.is_active ? `"${displayName}" will lose access immediately.` : `"${displayName}" will regain access.`}
          confirmLabel={user.is_active ? "Deactivate" : "Activate"}
          confirmColor={user.is_active ? "#dc2626" : "var(--g600)"}
          onConfirm={handleConfirmed}
          onCancel={() => setConfirmToggle(false)}
        />
      )}
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function SuperAdminUsers() {
  const [profiles,     setProfiles]     = useState([]);
  const [valets,       setValets]       = useState([]);
  const [properties,   setProperties]   = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState("");
  const [filterRole,   setFilterRole]   = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showModal,    setShowModal]    = useState(false);
  const [editUser,     setEditUser]     = useState(null);

  const propMap = Object.fromEntries(properties.map(p => [p.id, p.name]));

  const fetchData = async () => {
    setLoading(true);
    const [{ data: profs }, { data: props }, { data: vcs }] = await Promise.all([
      supabase.from("profiles").select("*").order("created_at", { ascending: false }),
      supabase.from("properties").select("id, name"),
      supabase.from("valet_codes").select("*").order("created_at", { ascending: false }),
    ]);
    setProfiles(profs || []);
    setProperties(props || []);
    setValets(vcs || []);
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const handleToggleProfile = async (id, newStatus) => {
    await supabase.from("profiles").update({ is_active: newStatus, updated_at: new Date() }).eq("id", id);
    fetchData();
  };

  const handleToggleValet = async (id, newStatus) => {
    await supabase.from("valet_codes").update({ is_active: newStatus }).eq("id", id);
    fetchData();
  };

  const showProfiles = filterRole !== "valet";
  const showValets   = filterRole === "all" || filterRole === "valet";

  const filteredProfiles = profiles.filter(u => {
    const q = search.toLowerCase();
    const matchSearch  = (u.email || "").toLowerCase().includes(q) || (u.full_name || "").toLowerCase().includes(q);
    const matchRole    = filterRole === "all" || filterRole === "valet" ? (filterRole !== "valet") : u.role === filterRole;
    const matchStatus  = filterStatus === "all" || (filterStatus === "active" ? u.is_active : !u.is_active);
    return matchSearch && (filterRole === "all" ? true : matchRole) && matchStatus;
  }).filter(u => filterRole === "valet" ? false : filterRole === "all" ? true : u.role === filterRole);

  const filteredValets = valets.filter(v => {
    const q = search.toLowerCase();
    const matchSearch = (v.code || "").toLowerCase().includes(q) || (v.label || "").toLowerCase().includes(q);
    const matchStatus = filterStatus === "all" || (filterStatus === "active" ? v.is_active : !v.is_active);
    return matchSearch && matchStatus;
  });

  const totalAll    = profiles.length + valets.length;
  const totalActive = profiles.filter(u => u.is_active).length + valets.filter(v => v.is_active).length;

  const roleFilters = [
    { key: "all",         label: "All",        color: "#333",         count: totalAll },
    { key: "super_admin", label: "Super Admin", color: "#6a1b9a",     count: profiles.filter(u => u.role === "super_admin").length },
    { key: "hoa_admin",   label: "HOA Admin",   color: "#1565c0",     count: profiles.filter(u => u.role === "hoa_admin").length },
    { key: "resident",    label: "Resident",    color: "var(--g600)", count: profiles.filter(u => u.role === "resident").length },
    { key: "valet",       label: "Valet",       color: "#e65100",     count: valets.length },
  ];

  return (
    <DashboardLayout role="super_admin" title="User Management" navItems={NAV}>

      {/* Header */}
      <div className="fu" style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase", color: "#6a1b9a", marginBottom: 6 }}>Super Admin</div>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 24, fontWeight: 500, color: "var(--ink)", margin: 0 }}>Users</h2>
            <p style={{ fontSize: 13, color: "var(--mut)", marginTop: 4 }}>{totalAll} total · {totalActive} active</p>
          </div>
          <button onClick={() => setShowModal(true)} style={{
            display: "flex", alignItems: "center", gap: 7, padding: "10px 18px", borderRadius: 10, border: "none",
            background: "var(--g600)", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer",
            boxShadow: "0 2px 8px rgba(34,107,65,.25)",
          }}>
            <Plus size={15} /> Create User
          </button>
        </div>
      </div>

      {/* Role filter tabs */}
      <div className="fu d1" style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {roleFilters.map(({ key, label, color, count }) => (
          <button key={key} onClick={() => setFilterRole(key)} style={{
            padding: "7px 14px", borderRadius: 20,
            border: `1.5px solid ${filterRole === key ? color : "var(--ln)"}`,
            background: filterRole === key ? color : "#fff",
            color: filterRole === key ? "#fff" : "var(--mut)",
            fontSize: 12, fontWeight: 700, cursor: "pointer",
            display: "flex", alignItems: "center", gap: 6,
          }}>
            {label}
            <span style={{
              background: filterRole === key ? "rgba(255,255,255,.25)" : "#f5f5f5",
              color: filterRole === key ? "#fff" : "var(--mut)",
              borderRadius: 10, padding: "1px 6px", fontSize: 10, fontWeight: 700,
            }}>{count}</span>
          </button>
        ))}
      </div>

      {/* Search + status */}
      <div className="fu d2" style={{ display: "flex", gap: 10, marginBottom: 18, flexWrap: "wrap" }}>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search name, email or valet code…"
          style={{ flex: 1, minWidth: 180, padding: "9px 13px", borderRadius: 8, fontSize: 13, border: "1.5px solid var(--ln)", background: "#fafafa", outline: "none", color: "var(--ink)" }}
        />
        {["all", "active", "inactive"].map(s => (
          <button key={s} onClick={() => setFilterStatus(s)} style={{
            padding: "9px 14px", borderRadius: 8, border: "1.5px solid var(--ln)",
            background: filterStatus === s ? "var(--g600)" : "#fff",
            color: filterStatus === s ? "#fff" : "var(--mut)",
            fontSize: 12, fontWeight: 700, cursor: "pointer", textTransform: "capitalize",
          }}>{s}</button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "48px 0", color: "var(--mut)", fontSize: 13 }}>Loading users…</div>
      ) : (
        <>
          {showProfiles && filteredProfiles.map(user => (
            <UserRow key={user.id} user={user} propMap={propMap}
              onToggle={handleToggleProfile} onEdit={setEditUser} isValet={false} />
          ))}

          {showValets && filteredValets.length > 0 && (
            <>
              {filterRole === "all" && (
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", color: "var(--mut)", margin: "16px 0 8px", display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ flex: 1, height: 1, background: "var(--ln)", display: "block" }} />
                  Valet Codes
                  <span style={{ flex: 1, height: 1, background: "var(--ln)", display: "block" }} />
                </div>
              )}
              {filteredValets.map(v => (
                <UserRow key={v.id} user={{ ...v, role: "valet" }} propMap={propMap}
                  onToggle={handleToggleValet} onEdit={() => {}} isValet />
              ))}
            </>
          )}

          {filteredProfiles.length === 0 && (showValets ? filteredValets.length === 0 : true) && (
            <div style={{ textAlign: "center", padding: "40px 24px", background: "#fff", borderRadius: 14, border: "1.5px dashed var(--ln)" }}>
              <Users size={30} color="var(--ln)" style={{ marginBottom: 12 }} />
              <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)", marginBottom: 6 }}>No users found</div>
              <div style={{ fontSize: 13, color: "var(--mut)" }}>Try adjusting your filters.</div>
            </div>
          )}
        </>
      )}

      {/* Valet note */}
      {showValets && (
        <div style={{ fontSize: 12, color: "#e65100", marginTop: 12, padding: "10px 14px", background: "#fff3e0", borderRadius: 8, border: "1px solid #ffb74d" }}>
          💡 To edit valet labels or codes, go to the <strong>Valet Codes</strong> page.
        </div>
      )}

      {showModal && (
        <UserModal mode="create" properties={properties} onClose={() => setShowModal(false)} onSaved={fetchData} />
      )}
      {editUser && (
        <UserModal mode="edit" user={editUser} properties={properties} onClose={() => setEditUser(null)} onSaved={fetchData} />
      )}

      <style>{`
        @keyframes fadeUp { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
        @keyframes spin   { to { transform: rotate(360deg); } }
      `}</style>
    </DashboardLayout>
  );
}